import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight, Presentation } from 'lucide-react';
import { WebinarEventsJsonLd } from '@/components/seo/WebinarEventsJsonLd';
import type { WebinarStatus } from '@/lib/webinar-api';

export const HOMEPAGE_WEBINAR_HORIZON_DAYS = 20;

export type HomeWebinar = {
  id: string;
  title: string;
  description: string;
  joinCode: string;
  status: WebinarStatus;
  scheduledStartAt: string | null;
  scheduledDurationMinutes: number | null;
  registrationUrl: string;
};

function formatWebinarDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
}

function formatDuration(minutes: number | null): string {
  if (typeof minutes !== 'number' || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h <= 0) return `${m} min`;
  return `${h} h${m > 0 ? ` ${String(m).padStart(2, '0')}` : ''}`;
}

function registrationHref(w: HomeWebinar): string {
  if (typeof window !== 'undefined' && w.joinCode) {
    return `${window.location.origin}/webinars/register/${encodeURIComponent(w.joinCode)}`;
  }
  return w.registrationUrl;
}

export function filterHomepageWebinars(
  webinars: HomeWebinar[],
  horizonDays = HOMEPAGE_WEBINAR_HORIZON_DAYS,
): HomeWebinar[] {
  const now = Date.now();
  const horizon = now + horizonDays * 24 * 60 * 60 * 1000;

  return webinars
    .filter((w) => {
      if (w.status === 'live') return true;
      if (!w.scheduledStartAt) return false;
      const start = new Date(w.scheduledStartAt).getTime();
      return !Number.isNaN(start) && start >= now && start <= horizon;
    })
    .sort((a, b) => {
      if (a.status === 'live' && b.status !== 'live') return -1;
      if (b.status === 'live' && a.status !== 'live') return 1;
      const ta = a.scheduledStartAt ? new Date(a.scheduledStartAt).getTime() : Number.MAX_SAFE_INTEGER;
      const tb = b.scheduledStartAt ? new Date(b.scheduledStartAt).getTime() : Number.MAX_SAFE_INTEGER;
      return ta - tb;
    });
}

function HomeWebinarCard({ webinar }: { webinar: HomeWebinar }) {
  const dateLabel = formatWebinarDate(webinar.scheduledStartAt);
  const durationLabel = formatDuration(webinar.scheduledDurationMinutes);
  const live = webinar.status === 'live';

  return (
    <article className="lo-home-webinar-card">
      <div className="lo-home-webinar-icon" aria-hidden>
        <Presentation className="h-5 w-5" />
      </div>
      <h3>{webinar.title}</h3>
      {webinar.description ? <p>{webinar.description}</p> : null}
      <div className="lo-home-webinar-meta">
        {live ? (
          <span className="lo-home-webinar-badge live">En direct</span>
        ) : dateLabel ? (
          <span className="lo-home-webinar-badge">
            <Calendar className="h-3.5 w-3.5" aria-hidden />
            {dateLabel}
          </span>
        ) : null}
        {durationLabel ? <span className="lo-home-webinar-badge">{durationLabel}</span> : null}
      </div>
      <Link to={registrationHref(webinar)} className="lo-btn lo-btn-primary lo-btn-sm lo-home-webinar-cta">
        {live ? 'Rejoindre' : 'S’inscrire'}
      </Link>
    </article>
  );
}

export function HomeWebinarsCarousel({ webinars }: { webinars: HomeWebinar[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const showNav = webinars.length > 1;

  const updateNav = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanPrev(track.scrollLeft > 4);
    setCanNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateNav();
    const track = trackRef.current;
    if (!track) return;
    track.addEventListener('scroll', updateNav, { passive: true });
    window.addEventListener('resize', updateNav);
    return () => {
      track.removeEventListener('scroll', updateNav);
      window.removeEventListener('resize', updateNav);
    };
  }, [webinars, updateNav]);

  const scrollByPage = (direction: -1 | 1) => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.querySelector<HTMLElement>('.lo-home-webinar-slide');
    const gap = 20;
    const step = slide ? slide.offsetWidth + gap : track.clientWidth * 0.9;
    track.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  if (!showNav) {
    return (
      <div className="lo-home-webinars-single">
        <HomeWebinarCard webinar={webinars[0]!} />
      </div>
    );
  }

  return (
    <div className="lo-home-webinars-carousel">
      <button
        type="button"
        className="lo-home-webinars-nav lo-home-webinars-nav--prev"
        onClick={() => scrollByPage(-1)}
        disabled={!canPrev}
        aria-label="Webinaire précédent"
      >
        <ChevronLeft className="h-5 w-5" aria-hidden />
      </button>

      <div className="lo-home-webinars-track" ref={trackRef}>
        {webinars.map((w) => (
          <div key={w.id} className="lo-home-webinar-slide">
            <HomeWebinarCard webinar={w} />
          </div>
        ))}
      </div>

      <button
        type="button"
        className="lo-home-webinars-nav lo-home-webinars-nav--next"
        onClick={() => scrollByPage(1)}
        disabled={!canNext}
        aria-label="Webinaire suivant"
      >
        <ChevronRight className="h-5 w-5" aria-hidden />
      </button>
    </div>
  );
}

export function PublicHomeWebinarsSection({ className = 'lo-section lo-section--home-webinars' }: { className?: string }) {
  const [webinars, setWebinars] = useState<HomeWebinar[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { loadPublicHomeWebinars } = await import('@/lib/public-home-webinars');
        const items = await loadPublicHomeWebinars();
        if (!cancelled) setWebinars(items);
      } catch {
        if (!cancelled) setWebinars([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded || webinars.length === 0) return null;

  const eventSchema = webinars.map((w) => ({
    title: w.title,
    description: w.description,
    startDate: w.scheduledStartAt,
    durationMinutes: w.scheduledDurationMinutes,
    url: registrationHref(w),
    status: w.status,
  }));

  return (
    <section className={className} aria-labelledby="lo-home-webinars-title" data-home-webinars-section="1">
      <WebinarEventsJsonLd events={eventSchema} />
      <div className="lo-wrap">
        <div className="lo-section-head">
          <span className="lo-kicker">Webinaires</span>
          <h2 id="lo-home-webinars-title" className="lo-h2">
            Prochains webinaires publics
          </h2>
          <p className="lo-lead">
            Inscrivez-vous aux sessions en direct organisées par nos formateurs (20 prochains jours).
          </p>
        </div>
        <HomeWebinarsCarousel webinars={webinars} />
      </div>
    </section>
  );
}
